import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Circle, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";

export default function SkillTree() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/skill-tree").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  if (!branches) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">Skill Tree</h1>
      <p className="text-muted-foreground mb-6">Master beginner topics to unlock advanced skills. Grow every branch of your adulthood.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        {branches.map((b, i) => {
          const col = catColor(b.color);
          const isOpen = open === b.branch;
          return (
            <motion.div key={b.branch} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className={`rounded-3xl glass border ${col.border} overflow-hidden self-start`}>
              <button data-testid={`branch-${b.branch}`} onClick={() => setOpen(isOpen ? null : b.branch)} className="w-full p-5 flex items-center gap-4 text-left">
                <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${col.grad} grid place-items-center shadow-lg shrink-0 relative`}>
                  <Icon name={b.icon} size={26} className="text-white" />
                  <span className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full bg-background border-2 border-background grid place-items-center">
                    <span className={`h-5 w-5 rounded-full ${col.solid} grid place-items-center text-[10px] font-black text-white`}>{b.level}</span>
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-bold text-base">{b.name}</h2>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${col.grad}`} style={{ width: `${b.progress}%` }} /></div>
                    <span className="text-[11px] font-bold text-muted-foreground">{b.completed}/{b.total}</span>
                  </div>
                </div>
                <ChevronDown size={18} className={`text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} className="px-5 pb-5 space-y-2">
                  {b.lessons.length === 0 && <p className="text-sm text-muted-foreground">Lessons coming soon in this branch.</p>}
                  {b.lessons.map((l) => (
                    <button key={l.id} data-testid={`skill-lesson-${l.id}`} onClick={() => navigate(`/lesson/${l.id}`)} className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 text-left transition-colors">
                      {l.completed ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0" /> : <Circle size={18} className="text-muted-foreground shrink-0" />}
                      <span className={`text-sm font-medium ${l.completed ? "text-muted-foreground" : ""}`}>{l.title}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
